import {useCallback, useState} from 'react';
import {AuthBox} from '../../../../../../components/auth';
import {Button} from '../../../../../../components/button';
import {Input} from '../../../../../../components/form';
import {LinkedinIcon} from '../../../../../../components/icons';
import {FindOutMoreContent} from '../../../../../../components/onboarding';
import {OnboardingCompany} from '../../../../../../types';
import {segmentTrack} from '../../../../../../utils';

type Props = {
  onSkip: () => void;
  onVerify: (url: string) => Promise<OnboardingCompany | undefined>;
  unverified: boolean;
};

export const ProvideCompanyUrl = ({onSkip, onVerify, unverified}: Props) => {
  const [url, setUrl] = useState('');
  const [lastVerifiedUrl, setLastVerifiedUrl] = useState<string | undefined>(undefined);
  const onVerifyCompany = useCallback(() => {
    setLastVerifiedUrl(url);
    onVerify(url);
  }, [url, onVerify]);

  const [isFindOutMoreOpen, setIsFindOutMoreOpen] = useState(false);
  const toggleFindOutMore = useCallback(() => {
    setIsFindOutMoreOpen(!isFindOutMoreOpen);
    segmentTrack('Button clicked', {
      label: 'find out more',
      location: 'verify company',
    });
  }, [isFindOutMoreOpen]);

  const isInvalid = unverified && lastVerifiedUrl === url;

  return (
    <AuthBox
      headerTitle="Verify Your Company"
      headerDescription={
        <>
          Add you company’s official LinkedIn URL.{' '}
          <a
            className="mx-auto mb-4 cursor-pointer text-swarm-gray-700 underline"
            onClick={toggleFindOutMore}
          >
            Learn more
          </a>
        </>
      }
    >
      <FindOutMoreContent
        visible={isFindOutMoreOpen}
        content={
          <>
            We need your Company LinkedIn URL for the AI Network Mapper process to analyze your team, identify
            key "Connectors," and map your company’s professional network for deeper insights.{' '}
            <a
              className="cursor-pointer text-swarm-gray-800 underline hover:text-swarm-black"
              href="https://help.theswarm.com/en/articles/9847654-ai-network-mapper"
              target="_blank"
              rel="nofollow noreferrer"
            >
              Read more
            </a>
            .
          </>
        }
      />

      <p className="text-left" data-intercom-target="company linkedin label">
        Company LinkedIn
      </p>
      <Input
        placeholder="Enter Your Company LinkedIn URL"
        onChange={e => setUrl(e.target.value)}
        isInvalid={isInvalid}
        icon={LinkedinIcon}
      />
      {isInvalid && (
        <p
          className="-mt-1 mb-2 text-left text-sm text-red-500"
          data-intercom-target="magical onboarding error"
        >
          We couldn't verify your company. Try again or skip to add later.
        </p>
      )}
      <div className="flex flex-col gap-2">
        <Button
          fullWidth
          variant="primary"
          onClick={onVerifyCompany}
          size="lg"
          tracking={{label: 'verify', location: 'company mapping'}}
        >
          Verify
        </Button>
        <Button
          fullWidth
          onClick={onSkip}
          variant="secondary"
          disableTitleCase
          size="lg"
          tracking={{label: 'skip for now', location: 'company mapping'}}
        >
          Skip for now
        </Button>
      </div>
    </AuthBox>
  );
};
